/**
 * Shared .docx -> editor-content ingest for the Notice Editor.
 *
 * The source project only ever loaded a .docx from the Data Fields file picker, so this
 * logic lived inline in VariableSidebar. JustAct-CM also needs to load a .docx
 * programmatically - the Notice Generation "Create Notice Template" step opens the
 * already-uploaded template for editing - so the parsing and the `<<variable>>`
 * extraction are factored out here and used by both paths. The extraction rules are the
 * source's, unchanged: data-label attributes on variable tags, plus bare `<<name>>`
 * occurrences in the text, de-duplicated and sorted naturally.
 */
import { parseTemplateSyntaxToHtml } from './templateParser';
import { convertDocxToHtml } from './docxToHtml';

/** Decodes the HTML entities Word/mammooth leave behind. */
const decodeEntities = (value) =>
  String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'");

/**
 * Turns raw mammoth HTML into the editor's HTML plus the variable names it references.
 * @param {string} html HTML produced by convertDocxToHtml()
 * @returns {{ html: string, variableNames: string[] }}
 */
export function extractTemplateFromHtml(html) {
  const parsedHtml = parseTemplateSyntaxToHtml(html);

  // Robust variable extraction: ONLY variables enclosed in << ... >>
  const extractedSet = new Set();

  // 1. Extract from parsed HTML data-label attributes (variable tags)
  const tagRegex = /data-label="([^"]+)"/g;
  let tagMatch;
  while ((tagMatch = tagRegex.exec(parsedHtml)) !== null) {
    const clean = decodeEntities(tagMatch[1])
      .replace(/<[^>]+>/g, '')
      .trim();
    if (
      clean &&
      !clean.includes('||') &&
      !clean.includes('==') &&
      !clean.includes('<') &&
      !clean.includes('>') &&
      clean.length < 100
    ) {
      extractedSet.add(clean);
    }
  }

  // 2. Extract strictly from text matching << variable_name >>
  const plainText = decodeEntities(
    String(html || '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>\s*<p[^>]*>/gi, ' '),
  );
  const regex = /<<([^<>]+)>>/g;
  let match;
  while ((match = regex.exec(plainText)) !== null) {
    const clean = decodeEntities(match[1])
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/ /g, ' ')
      .replace(/\*\*/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (
      clean &&
      !clean.includes('==') &&
      !clean.includes('||') &&
      !clean.includes('<') &&
      !clean.includes('>') &&
      !clean.startsWith('http') &&
      clean.length < 100
    ) {
      extractedSet.add(clean);
    }
  }

  const variableNames = Array.from(extractedSet).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );

  return { html: parsedHtml, variableNames };
}

/** Reads a File/Blob into an ArrayBuffer. */
function readAsArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result);
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Loads a .docx from a File/Blob/ArrayBuffer and returns editor-ready content.
 * @param {File|Blob|ArrayBuffer} source
 * @returns {Promise<{ html: string, variableNames: string[] }>}
 */
export async function ingestDocx(source) {
  if (!source) throw new Error('No document supplied');
  const arrayBuffer =
    source instanceof ArrayBuffer ? source : await readAsArrayBuffer(source);
  const html = await convertDocxToHtml(arrayBuffer);
  return extractTemplateFromHtml(html);
}

/**
 * Loads a .docx from a URL (e.g. the S3 location the Notice Generation form stores).
 * @param {string} url
 * @returns {Promise<{ html: string, variableNames: string[] }>}
 */
export async function ingestDocxFromUrl(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not download the template (HTTP ${response.status})`);
  }
  return ingestDocx(await response.arrayBuffer());
}
